import React, { useState, useEffect, useRef } from 'react';
import './FlipClock.css';

// Helper component for a single digit
const FlipDigit = ({ currentDigit, prevDigit, flipping }) => (
  <div className={`flap-card ${flipping ? 'flipping' : ''}`}>
    {/* --- Static Layers --- */}
    {/* Bottom half (old), shown during first half of flip */}
    <div className="flap-bottom-static-old">
      <div className="digit-line mask-bottom">{prevDigit}</div>
    </div>
    {/* Bottom half (new), shown by default and during second half of flip */}
    <div className="flap-bottom-static-new">
      <div className="digit-line mask-bottom">{currentDigit}</div>
    </div>
    {/* Top half (new), always static under the flipper */}
    <div className="flap-top-static-new">
      <div className="digit-line mask-top">{currentDigit}</div>
    </div>

    <div className="seam-soften" />

    {/* --- The Flipping Element --- */}
    <div className="flap-top">
      <div className="flap-front">
        <div className="digit-line mask-top">{prevDigit}</div>
      </div>
      <div className="flap-back">
        <div className="digit-line mask-top">{currentDigit}</div>
      </div>
    </div>
    
    <div className="flap-divider"></div>
    <div className="flap-frame"></div>
    <div className="flap-shadow"></div>
  </div>
);


const FlipClock = ({ time, enableSound = false }) => {
  const [prevTime, setPrevTime] = useState(time);
  const [flipping, setFlipping] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const clackRef = useRef(null);

  const formatNumber = (num) => {
    return num.toString().padStart(2, '0');
  };

  const currentHours = formatNumber(time.getHours());
  const currentMinutes = formatNumber(time.getMinutes());
  const currentSeconds = formatNumber(time.getSeconds());
  
  const prevHours = formatNumber(prevTime.getHours());
  const prevMinutes = formatNumber(prevTime.getMinutes());
  const prevSeconds = formatNumber(prevTime.getSeconds());

  // Optional sound effect
  useEffect(() => {
    if (!enableSound) return;
    try {
      clackRef.current = new Audio('/sounds/clack.mp3');
      clackRef.current.preload = 'auto';
      clackRef.current.volume = 0.5;
    } catch (e) {
      // ignore audio init errors
    }
  }, [enableSound]);

  // Initial animation effect
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      const initFlipping = {
        hour1: true, hour2: true, min1: true, min2: true, sec1: true, sec2: true
      };
      setFlipping(initFlipping);
      setTimeout(() => setFlipping({}), 2000);
    }
  }, [isInitialized]);

  // Detect changes and trigger animations
  useEffect(() => {
    if (!isInitialized) return;
    
    const newFlipping = {};
    
    if (currentHours !== prevHours) {
      if (currentHours[0] !== prevHours[0]) newFlipping.hour1 = true;
      if (currentHours[1] !== prevHours[1]) newFlipping.hour2 = true;
    }
    if (currentMinutes !== prevMinutes) {
      if (currentMinutes[0] !== prevMinutes[0]) newFlipping.min1 = true;
      if (currentMinutes[1] !== prevMinutes[1]) newFlipping.min2 = true;
    }
    if (currentSeconds !== prevSeconds) {
      if (currentSeconds[0] !== prevSeconds[0]) newFlipping.sec1 = true;
      if (currentSeconds[1] !== prevSeconds[1]) newFlipping.sec2 = true;
    }
    
    if (Object.keys(newFlipping).length > 0) {
      setFlipping(newFlipping);
      const flipTotalMs = 520; // Matches CSS --flip-total
      if (enableSound && clackRef.current) {
        setTimeout(() => {
          try { clackRef.current.currentTime = 0; clackRef.current.play(); } catch (_) {}
        }, Math.floor(flipTotalMs / 2));
      }
      setTimeout(() => setFlipping({}), flipTotalMs);
    }
    
    setPrevTime(time);
  }, [time, prevTime, isInitialized]);

  return (
    <div className="changi-flap-display">
      <div className="clock-display">
        <div className="flip-unit">
          <FlipDigit currentDigit={currentHours[0]} prevDigit={prevHours[0]} flipping={flipping.hour1} />
          <FlipDigit currentDigit={currentHours[1]} prevDigit={prevHours[1]} flipping={flipping.hour2} />
        </div>
        
        <div className="separator">:</div>
        
        <div className="flip-unit">
          <FlipDigit currentDigit={currentMinutes[0]} prevDigit={prevMinutes[0]} flipping={flipping.min1} />
          <FlipDigit currentDigit={currentMinutes[1]} prevDigit={prevMinutes[1]} flipping={flipping.min2} />
        </div>
        
        <div className="separator">:</div>
        
        <div className="flip-unit">
          <FlipDigit currentDigit={currentSeconds[0]} prevDigit={prevSeconds[0]} flipping={flipping.sec1} />
          <FlipDigit currentDigit={currentSeconds[1]} prevDigit={prevSeconds[1]} flipping={flipping.sec2} />
        </div>
      </div>
    </div>
  );
};

export default FlipClock;
